import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const SSO_PKCE_KEY = 'sso_pkce_verifier'

/**
 * SSO Callback Page
 *
 * Handles the OAuth callback from Cognito Hosted UI.
 * Exchanges the authorization code server-side via POST /auth/sso/exchange
 * so auth.coformit.dev/oauth2/token is never called from the browser.
 */
const SsoCallbackPage = () => {
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, loading, completeSsoLogin } = useAuth()
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Check for OAuth error in URL
    const urlError = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (urlError) {
      console.error('SSO error from URL:', urlError, errorDescription)
      setError(errorDescription || 'SSO authentication failed')
      return
    }

    // Get the authorization code
    const code = searchParams.get('code')
    if (!code) {
      if (user) {
        navigate('/dashboard', { replace: true })
      } else if (!loading) {
        setError('No authorization code received')
      }
      return
    }

    const processCallback = async () => {
      if (hasProcessed.current) return
      hasProcessed.current = true

      try {
        // Retrieve PKCE verifier stored before the SSO redirect
        const codeVerifier = sessionStorage.getItem(SSO_PKCE_KEY)
        sessionStorage.removeItem(SSO_PKCE_KEY)

        if (!codeVerifier) {
          throw new Error('PKCE verifier not found — OAuth flow may have been interrupted')
        }

        const redirectUri = `${window.location.origin}/auth/callback`

        // Exchange code server-side (hides token call from browser network tab)
        const result = await completeSsoLogin(code, codeVerifier, redirectUri)

        if (!result.success) {
          throw new Error(result.error || 'Failed to complete SSO authentication')
        }

        navigate('/dashboard', { replace: true })
      } catch (err) {
        console.error('SSO callback error:', err)
        setError(err.message || 'Failed to complete SSO authentication')
        hasProcessed.current = false
      }
    }

    if (!loading && !user) {
      processCallback()
    } else if (user) {
      navigate('/dashboard', { replace: true })
    }
  }, [searchParams, navigate, user, loading, completeSsoLogin])

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '1rem'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          padding: '2rem',
          textAlign: 'center',
          border: '1px solid #ccc',
          borderRadius: '8px'
        }}>
          <h2 style={{ color: '#c00', marginBottom: '1rem' }}>Sign-in Failed</h2>
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            style={{ padding: '0.75rem 1.5rem' }}
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh'
    }}>
      <div style={{ textAlign: 'center' }}>
        <p>Completing sign-in...</p>
      </div>
    </div>
  )
}

export default SsoCallbackPage
